import { type ComponentChildren } from 'preact';
import * as stylex from '@stylexjs/stylex';
import { styles } from './form.styles';
interface FormProps {
  /** Width behaviour. `fill` (default) takes the container — right inside a Modal
   *  or a Card. `narrow` caps at 460px, for a form sitting directly on an open
   *  page where a full-width text field would run past readability. */
  width?: 'fill' | 'narrow';
  /** Render as a real `<form>` and call this on submit. Without it the stack is a
   *  plain `<div>` — which is what a dialog driven by its own footer buttons
   *  wants, since a nested `<form>` there submits nothing. */
  onSubmit?: ((e: Event) => void) | undefined;
  children: ComponentChildren;
}

/**
 * The vertical field stack every form in this console uses (R-304).
 *
 * Holds no width of its own by default: the container decides. That is the fix
 * for the 21 copy-pasted `form:` styles whose four different `maxWidth` values
 * left a different dead gutter in each module's dialogs.
 */
export function Form({
  width = 'fill',
  onSubmit,
  children
}: FormProps) {
  const props = stylex.props(styles.form, width === 'narrow' && styles.narrow);
  if (onSubmit == null) return <div {...props}>{children}</div>;
  return <form {...props} onSubmit={e => {
    e.preventDefault();
    onSubmit(e);
  }}>
      {children}
    </form>;
}
interface FormRowProps {
  /** Columns on a wide viewport; always one on a narrow one. */
  cols?: 2 | 3;
  children: ComponentChildren;
}

/** Two (or three) fields side by side, collapsing to one column at ≤640px. */
export function FormRow({
  cols = 2,
  children
}: FormRowProps) {
  return <div {...stylex.props(cols === 3 ? styles.rowThirds : styles.row)}>{children}</div>;
}
interface FormActionsProps {
  /** `end` (default) right-aligns the cluster; `between` splits a destructive
   *  action away from the commit pair; `start` leaves it against the fields. */
  align?: 'start' | 'end' | 'between';
  /** A rule above the buttons — worth it on a long form, noise on a short one. */
  divided?: boolean;
  children: ComponentChildren;
}

/** The footer button cluster of a form or dialog. */
export function FormActions({
  align = 'end',
  divided = false,
  children
}: FormActionsProps) {
  return <div {...stylex.props(styles.actions, align === 'end' && styles.actionsEnd, align === 'between' && styles.actionsBetween, divided && styles.actionsDivided)}>
      {children}
    </div>;
}